/** Maker-checker approvals inbox (doc 11 §5). */
import { useCallback, useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { SpecDiffEntry } from '@servicesai/api-client';
import {
  EmptyState,
  FilterBar,
  GlassButton,
  GlassCard,
  GlassInput,
  StatusPill,
  formatDate,
  useI18n,
} from '@servicesai/ui';
import { admin } from '../lib/api.js';
import { S } from '../strings.js';
import { HeroBand, MetricTile, SectionCard } from '../components/elevated/kit.js';
import { Icon } from '../components/elevated/icons.js';
import { SECTION_ACCENTS } from '../lib/accents.js';

const COPY = {
  pendingApprovals: { en: 'Pending approvals', ar: 'طلبات بانتظار الاعتماد' },
  awaitingChecker: { en: 'Awaiting a checker decision', ar: 'بانتظار قرار المدقق' },
  criticalRisk: { en: 'Critical risk', ar: 'مخاطر حرجة' },
  criticalHint: { en: 'Require immediate attention', ar: 'تتطلب انتباهاً فورياً' },
};

interface ApprovalItem {
  id: string;
  kind: 'service_publish' | 'service_lifecycle' | 'decision_notice' | 'refund' | 'template_change' | 'vault_correction' | 'delegation_grant' | 'break_glass' | 'ops_remediation';
  subject_table: string;
  subject_id: string;
  payload: Record<string, unknown>;
  maker_name: string;
  created_at: string;
  expires_at: string;
  risk_tier?: string;
}

interface AuditEntryRow {
  id: number;
  ts: string;
  actor: Record<string, unknown>;
  action: string;
  subject: Record<string, unknown>;
  detail: Record<string, unknown>;
}

/* Risk tiers ordered from safest to most severe; unknown tiers slot in the middle. */
const TIER_RANK: Record<string, number> = {
  low: 0,
  standard: 1,
  medium: 2,
  elevated: 3,
  high: 4,
  critical: 5,
};

const TIER_TONE: Record<string, string> = {
  low: '#3f8e50',
  standard: '#0173ab',
  medium: '#b68a35',
  elevated: '#b2550a',
  high: '#d83731',
  critical: '#b52520',
};

const tierOf = (item: ApprovalItem): string => item.risk_tier ?? 'standard';
const tierRank = (tier: string): number => TIER_RANK[tier] ?? 2;

function tierLabel(tier: string) {
  if (tier === 'low') return S.tierLow;
  if (tier === 'standard') return S.tierStandard;
  if (tier === 'medium') return S.tierMedium;
  if (tier === 'elevated') return S.tierElevated;
  if (tier === 'high') return S.tierHigh;
  if (tier === 'critical') return S.tierCritical;
  return { en: tier, ar: tier };
}

/** Display reference for an approval — payload reference/slug, else the subject id. */
function itemRef(item: ApprovalItem): string {
  const payload = item.payload as { reference?: unknown; slug?: unknown };
  if (typeof payload.reference === 'string' && payload.reference) return payload.reference;
  if (typeof payload.slug === 'string' && payload.slug) return payload.slug;
  return item.subject_id;
}

export function ApprovalsPage() {
  const { l, lang } = useI18n();
  const [items, setItems] = useState<ApprovalItem[] | null>(null);
  const [error, setError] = useState(false);
  const [toast, setToast] = useState<string | null>(null);
  const [flash, setFlash] = useState<string | null>(null);
  const [rejecting, setRejecting] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [kindFilter, setKindFilter] = useState('');
  const [tierFilter, setTierFilter] = useState('');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [confirmBulk, setConfirmBulk] = useState(false);
  const [bulkProgress, setBulkProgress] = useState<{ done: number; total: number } | null>(null);
  const [auditItem, setAuditItem] = useState<ApprovalItem | null>(null);

  const load = useCallback(() => {
    setError(false);
    admin()
      .approvals()
      .then((res) => {
        const next = res.items as unknown as ApprovalItem[];
        setItems(next);
        setSelected((prev) => new Set([...prev].filter((id) => next.some((item) => item.id === id))));
      })
      .catch(() => setError(true));
  }, []);

  useEffect(load, [load]);

  const decide = async (id: string, decision: 'approve' | 'reject', decisionNote?: string) => {
    try {
      await admin().decideApproval(id, decision, decisionNote);
      setToast(null);
      setRejecting(null);
      setNote('');
      load();
    } catch (err) {
      setToast(`${l(S.actionFailed)}: ${(err as Error).message}`);
    }
  };

  const kindFiltered = (items ?? []).filter((item) => !kindFilter || item.kind === kindFilter);
  const kinds = [...new Set((items ?? []).map((item) => item.kind))].sort();
  const tiers = [...new Set((items ?? []).map(tierOf))].sort(
    (a, b) => tierRank(a) - tierRank(b) || a.localeCompare(b),
  );
  /* Bulk approval is guarded to the safest tier present in the inbox. */
  const eligibleTier = tiers.includes('low') ? 'low' : tiers[0];
  /* Oldest first so the longest-waiting request is always on top. */
  const visibleItems = kindFiltered
    .filter((item) => !tierFilter || tierOf(item) === tierFilter)
    .sort((a, b) => Date.parse(a.created_at) - Date.parse(b.created_at));
  const criticalCount = visibleItems.filter((item) => item.risk_tier === 'critical').length;
  const bulkTargets = eligibleTier
    ? visibleItems.filter((item) => selected.has(item.id) && tierOf(item) === eligibleTier)
    : [];

  const toggleSelect = (id: string) =>
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  const runBulk = async () => {
    const targets = bulkTargets;
    setConfirmBulk(false);
    if (targets.length === 0) return;
    setFlash(null);
    setBulkProgress({ done: 0, total: targets.length });
    let failures = 0;
    for (const target of targets) {
      try {
        // Same single-item mutation as the per-row approve action, run sequentially.
        await admin().decideApproval(target.id, 'approve');
      } catch (err) {
        failures += 1;
        setToast(`${l(S.actionFailed)}: ${itemRef(target)} — ${(err as Error).message}`);
      }
      setBulkProgress((prev) => (prev ? { ...prev, done: prev.done + 1 } : prev));
    }
    setBulkProgress(null);
    setSelected(new Set());
    if (failures === 0) setFlash(l(S.bulkDone));
    load();
  };

  return (
    <div>
      <HeroBand
        eyebrow={l(S.navOperations)}
        title={l(S.approvalsTitle)}
        subtitle={l(S.approvalsHint)}
        status={<span className="aegov-badge badge-warning">{visibleItems.length} {l({ en: 'pending', ar: 'قيد الانتظار' })}</span>}
        metrics={
          <div className="grid gap-3 sm:grid-cols-2">
            <MetricTile
              dark
              label={l(COPY.pendingApprovals)}
              value={items === null ? '—' : visibleItems.length}
              hint={l(COPY.awaitingChecker)}
              accent="#cba344"
            />
            <MetricTile
              dark
              label={l(COPY.criticalRisk)}
              value={items === null ? '—' : criticalCount}
              hint={criticalCount > 0 ? l(COPY.criticalHint) : undefined}
              accent={criticalCount > 0 ? '#d83731' : '#3f8e50'}
            />
          </div>
        }
      />
      <FilterBar summary={`${visibleItems.length} ${l(S.rows)}`}>
        <div className="aegov-form-control control-sm">
          <div className="form-control-input">
            <select value={kindFilter} onChange={(event) => setKindFilter(event.target.value)} aria-label={l(S.kind)}>
              <option value="">{l(S.kind)}: {l(S.all)}</option>
              {kinds.map((kind) => <option key={kind} value={kind}>{kind.replaceAll('_', ' ')}</option>)}
            </select>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-1.5" role="group" aria-label={l(S.riskTier)}>
          <TierChip
            active={tierFilter === ''}
            onClick={() => setTierFilter('')}
            label={l(S.all)}
            count={kindFiltered.length}
          />
          {tiers.map((tier) => (
            <TierChip
              key={tier}
              active={tierFilter === tier}
              onClick={() => setTierFilter(tierFilter === tier ? '' : tier)}
              label={l(tierLabel(tier))}
              count={kindFiltered.filter((item) => tierOf(item) === tier).length}
              tone={TIER_TONE[tier] ?? '#9ea2a9'}
            />
          ))}
        </div>
        <span className="ms-auto hidden text-[10px] font-semibold text-ink-faint lg:inline">
          {l(S.oldestFirst)}
        </span>
      </FilterBar>

      {eligibleTier && visibleItems.some((item) => tierOf(item) === eligibleTier) && (
        <div className="mb-3 flex flex-wrap items-center gap-x-4 gap-y-2 rounded-2xl border border-aegold-200 bg-aegold-50/60 px-4 py-2.5">
          <span className="text-xs font-bold">
            {selected.size} {l(S.selected)}
          </span>
          <GlassButton
            variant="primary"
            className="!py-2 text-xs"
            disabled={bulkTargets.length === 0 || bulkProgress !== null}
            onClick={() => setConfirmBulk(true)}
          >
            ✓ {l(S.approveSelected)} ({bulkTargets.length})
          </GlassButton>
          {bulkProgress && (
            <span role="status" className="text-xs font-bold text-gold-deep">
              {l(S.bulkProgress)} {bulkProgress.done}/{bulkProgress.total}…
            </span>
          )}
          <span className="text-[10px] text-ink-faint lg:ms-auto">{l(S.bulkLowOnly)}</span>
        </div>
      )}

      {toast && (
        <div
          className="mb-3 cursor-pointer rounded-2xl border border-reject/40 bg-reject/10 px-4 py-2 text-xs font-bold text-reject"
          onClick={() => setToast(null)}
        >
          {toast}
        </div>
      )}

      {flash && (
        <div
          role="status"
          className="mb-3 cursor-pointer rounded-2xl border border-approve/40 bg-approve/10 px-4 py-2 text-xs font-bold text-approve"
          onClick={() => setFlash(null)}
        >
          ✓ {flash}
        </div>
      )}

      {error && <div role="alert" className="aegov-alert alert-error"><div className="alert-content text-sm">{l(S.loadFailed)}</div></div>}

      {items && items.length === 0 && !error && (
        <GlassCard>
          <EmptyState title={l(S.emptyApprovals)} />
        </GlassCard>
      )}

      {items && items.length > 0 && visibleItems.length === 0 && !error && (
        <GlassCard>
          <EmptyState title={l(S.emptyQueue)} />
        </GlassCard>
      )}

      <div className="space-y-3">
        {visibleItems.map((item) => (
          <GlassCard key={item.id} className="p-5" strong>
            <div className="flex flex-wrap items-center gap-x-5 gap-y-2">
              {eligibleTier !== undefined && tierOf(item) === eligibleTier && (
                <input
                  type="checkbox"
                  aria-label={`${l(S.selectForBulk)}: ${itemRef(item)}`}
                  checked={selected.has(item.id)}
                  onChange={() => toggleSelect(item.id)}
                  className="h-4 w-4 shrink-0 accent-aeblack-800"
                />
              )}
              <StatusPill
                state={item.kind === 'break_glass' ? 'rejected' : item.kind === 'service_publish' ? 'published' : 'in_review'}
                label={item.kind === 'service_publish' ? l(S.kindServicePublish) : item.kind === 'decision_notice' ? l(S.kindDecision) : item.kind.replaceAll('_', ' ')}
              />
              <div className="text-xs text-ink-soft">
                <span className="text-ink-faint">{l(S.maker)}: </span>
                <span className="font-semibold">{item.maker_name}</span>
              </div>
              <div className="text-xs text-ink-soft">
                <span className="text-ink-faint">{l(S.expires)}: </span>
                {formatDate(item.expires_at, lang)}
              </div>
              <StatusPill state={item.risk_tier === 'critical' ? 'rejected' : item.risk_tier === 'elevated' ? 'in_review' : 'draft'} label={item.risk_tier ?? 'standard'} />
              <AgeBadge createdAt={item.created_at} />
            </div>

            <PayloadSummary item={item} />

            <div className="mt-4 flex flex-wrap items-center gap-2 border-t border-aeblack-100 pt-3">
              <GlassButton variant="primary" className="!py-2 text-xs" onClick={() => void decide(item.id, 'approve')}>
                ✓ {l(S.approve)}
              </GlassButton>
              <GlassButton
                variant="danger"
                className="!py-2 text-xs"
                onClick={() => setRejecting(rejecting === item.id ? null : item.id)}
              >
                ✗ {l(S.reject)}
              </GlassButton>
              <button
                type="button"
                onClick={() => setAuditItem(item)}
                aria-label={`${l(S.viewAudit)}: ${itemRef(item)}`}
                title={l(S.viewAudit)}
                className="grid h-8 w-8 cursor-pointer place-items-center rounded-lg border border-aeblack-200 text-ink-soft transition hover:border-seablue-600 hover:text-seablue-700"
              >
                <Icon name="audit" size={14} />
              </button>
              {rejecting === item.id && (
                <>
                  <GlassInput
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder={l(S.rejectNote)}
                    className="max-w-xs"
                  />
                  <GlassButton
                    className="!py-2 text-xs"
                    disabled={!note.trim()}
                    onClick={() => void decide(item.id, 'reject', note.trim())}
                  >
                    →
                  </GlassButton>
                </>
              )}
            </div>
          </GlassCard>
        ))}
      </div>

      {confirmBulk && (
        <BulkApproveDialog
          refs={bulkTargets.map(itemRef)}
          busy={bulkProgress !== null}
          onConfirm={() => void runBulk()}
          onCancel={() => setConfirmBulk(false)}
        />
      )}
      {auditItem && <ApprovalAuditDrawer item={auditItem} onClose={() => setAuditItem(null)} />}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * TierChip — risk-tier filter chip with a live count (plan point 31).
 * ------------------------------------------------------------------------- */
function TierChip({
  active,
  onClick,
  label,
  count,
  tone,
}: {
  active: boolean;
  onClick: () => void;
  label: string;
  count: number;
  tone?: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={`inline-flex min-h-8 cursor-pointer items-center gap-1.5 rounded-full border px-3 text-xs font-bold transition ${
        active
          ? 'border-aeblack-900 bg-aeblack-900 text-white'
          : 'border-aeblack-200 bg-white text-ink-soft hover:border-aeblack-300 hover:text-ink'
      }`}
    >
      {tone && <span aria-hidden className="h-2 w-2 rounded-full" style={{ backgroundColor: tone }} />}
      {label}
      <span className={`font-mono text-[10px] ${active ? 'text-aeblack-200' : 'text-ink-faint'}`}>{count}</span>
    </button>
  );
}

/* --------------------------------------------------------------------------
 * AgeBadge — SLA age chip: gold past 48h, red past 96h (plan point 33).
 * ------------------------------------------------------------------------- */
function AgeBadge({ createdAt }: { createdAt: string }) {
  const { l } = useI18n();
  const ageHours = Math.max(0, (Date.now() - Date.parse(createdAt)) / 3_600_000);
  const text =
    ageHours >= 24
      ? `${Math.floor(ageHours / 24)}${l(S.ageDay)}`
      : ageHours >= 1
        ? `${Math.floor(ageHours)}${l(S.ageHour)}`
        : `${Math.max(1, Math.floor(ageHours * 60))}${l(S.ageMinute)}`;
  const title = ageHours > 96 ? l(S.ageOver96) : ageHours > 48 ? l(S.ageOver48) : l(S.ageLabel);
  const tone = ageHours > 96 ? 'badge-error' : ageHours > 48 ? 'badge-warning' : '';
  return (
    <span title={title} className={`aegov-badge inline-flex items-center gap-1 ${tone}`}>
      <Icon name="clock" size={11} />
      {text}
    </span>
  );
}

/* --------------------------------------------------------------------------
 * BulkApproveDialog — bilingual confirm listing every reference about to be
 * approved (plan point 31). Closes on Esc/backdrop/Cancel.
 * ------------------------------------------------------------------------- */
function BulkApproveDialog({
  refs,
  busy,
  onConfirm,
  onCancel,
}: {
  refs: string[];
  busy: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  const { l } = useI18n();
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onCancel();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onCancel]);

  return (
    <div className="fixed inset-0 z-50 grid place-items-center p-4" role="dialog" aria-modal="true" aria-label={l(S.bulkConfirmTitle)}>
      <button
        type="button"
        aria-label={l(S.close)}
        onClick={onCancel}
        className="absolute inset-0 h-full w-full cursor-default bg-aeblack-900/45 backdrop-blur-[2px]"
      />
      <div className="relative w-full max-w-md rounded-2xl border border-aeblack-100 bg-white p-5 shadow-2xl">
        <h2 className="text-base font-extrabold">{l(S.bulkConfirmTitle)}</h2>
        <p className="mt-1 text-xs leading-relaxed text-ink-soft">{l(S.bulkConfirmHint)}</p>
        <ul className="mt-3 max-h-56 space-y-1 overflow-y-auto rounded-xl bg-aeblack-50 p-3">
          {refs.slice(0, 12).map((ref) => (
            <li key={ref} className="font-mono text-xs font-bold" dir="ltr">{ref}</li>
          ))}
          {refs.length > 12 && (
            <li className="text-[10px] text-ink-faint">+{refs.length - 12} {l(S.more)}</li>
          )}
        </ul>
        <div className="mt-4 flex justify-end gap-2">
          <GlassButton variant="ghost" onClick={onCancel} disabled={busy}>{l(S.cancel)}</GlassButton>
          <GlassButton variant="primary" onClick={onConfirm} disabled={busy}>
            ✓ {l(S.confirm)} ({refs.length})
          </GlassButton>
        </div>
      </div>
    </div>
  );
}

/* --------------------------------------------------------------------------
 * ApprovalAuditDrawer — side panel with the hash-chained audit entries whose
 * subject matches this approval (or the approval id itself), reusing the
 * admin audit-recent endpoint (plan point 34). Closes on X/Esc/backdrop.
 * ------------------------------------------------------------------------- */
function ApprovalAuditDrawer({ item, onClose }: { item: ApprovalItem; onClose: () => void }) {
  const { l } = useI18n();
  useEffect(() => {
    const onKey = (event: globalThis.KeyboardEvent) => {
      if (event.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  const query = useQuery({
    queryKey: ['admin-audit-recent'],
    queryFn: () => admin().auditRecent(),
  });
  const rows = (query.data?.items ?? []) as unknown as AuditEntryRow[];
  const related = rows.filter((row) => {
    const subjectId = String(row.subject?.id ?? '');
    const subjectTable = String(row.subject?.table ?? '');
    return (
      subjectId === item.id ||
      (subjectTable === item.subject_table && subjectId === item.subject_id)
    );
  });

  return (
    <div className="fixed inset-0 z-50" role="dialog" aria-modal="true" aria-label={l(S.auditTitle)}>
      <button
        type="button"
        aria-label={l(S.close)}
        onClick={onClose}
        className="absolute inset-0 h-full w-full cursor-default bg-aeblack-900/45 backdrop-blur-[2px]"
      />
      <aside className="absolute inset-y-0 right-0 flex h-full w-full max-w-md flex-col overflow-y-auto border-s border-aeblack-100 bg-white shadow-2xl">
        <div className="flex items-start justify-between gap-3 border-b border-aeblack-100 px-5 py-4">
          <div>
            <h2 className="text-base font-extrabold">{l(S.auditTitle)}</h2>
            <p className="mt-0.5 text-[11px] text-ink-faint">{l(S.auditHint)}</p>
            <code className="mt-1 inline-block font-mono text-[10px] font-bold text-seablue-700" dir="ltr">
              {itemRef(item)}
            </code>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label={l(S.close)}
            className="grid h-8 w-8 shrink-0 cursor-pointer place-items-center rounded-lg text-ink-faint transition hover:bg-aeblack-50 hover:text-ink"
          >
            <Icon name="x" size={16} />
          </button>
        </div>
        <div className="flex-1 space-y-3 px-5 py-4">
          {query.isLoading && (
            <div className="space-y-2" aria-busy="true">
              {[0, 1, 2].map((index) => (
                <div key={index} className="h-16 animate-pulse rounded-xl bg-aeblack-50" aria-hidden />
              ))}
            </div>
          )}
          {query.isError && (
            <div role="alert" className="aegov-alert alert-error"><div className="alert-content text-sm">{l(S.loadFailed)}</div></div>
          )}
          {!query.isLoading && !query.isError && related.length === 0 && (
            <EmptyState title={l(S.auditEmpty)} />
          )}
          {related.map((entry) => (
            <article key={entry.id} className="rounded-xl border border-aeblack-100 p-3">
              <div className="flex flex-wrap items-center gap-2">
                <code className="text-xs font-bold">{entry.action}</code>
                <time className="ms-auto text-[10px] text-ink-faint">
                  {new Date(entry.ts).toLocaleString()}
                </time>
              </div>
              <div className="mt-1 text-[10px] text-ink-faint">
                {l(S.agentAuditActor)}: {String(entry.actor?.kind ?? 'system')}
                <span className="ms-1 font-mono" dir="ltr">{String(entry.actor?.id ?? '—').slice(0, 16)}</span>
              </div>
              {entry.detail && Object.keys(entry.detail).length > 0 && (
                <pre className="mt-2 max-h-28 overflow-auto whitespace-pre-wrap break-all rounded-lg bg-aeblack-50 p-2 text-[9px] text-ink-soft" dir="ltr">
                  {JSON.stringify(entry.detail, null, 1).slice(0, 500)}
                </pre>
              )}
            </article>
          ))}
        </div>
      </aside>
    </div>
  );
}

function PayloadSummary({ item }: { item: ApprovalItem }) {
  const { l } = useI18n();
  if (item.kind === 'service_publish') {
    const payload = item.payload as {
      slug?: string;
      changelog?: string;
      goldenRun?: { passed: boolean; generatedAt: string; scenarios: { id: string; lang: string; status: string; assertions: string[] }[] };
    };
    return <ServicePublishDiff slug={payload.slug ?? ''} changelog={payload.changelog} goldenRun={payload.goldenRun} />;
  }
  if (item.kind === 'service_lifecycle') {
    const payload = item.payload as { slug?: string; action?: string; reason?: string; previousStatus?: string };
    return (
      <div className="mt-3 grid gap-3 rounded-xl bg-aeblack-50 p-3 text-xs sm:grid-cols-4">
        <div><span className="block text-[10px] text-ink-faint uppercase">Service</span><span className="font-mono font-bold">{payload.slug}</span></div>
        <div><span className="block text-[10px] text-ink-faint uppercase">Action</span><span className="font-bold">{payload.action}</span></div>
        <div><span className="block text-[10px] text-ink-faint uppercase">Current state</span><span className="font-bold">{payload.previousStatus}</span></div>
        <div><span className="block text-[10px] text-ink-faint uppercase">{l(S.reason)}</span><span>{payload.reason}</span></div>
      </div>
    );
  }
  const payload = item.payload as { outcome?: string; reasons?: { en: string; ar: string }[] };
  return (
    <div className="mt-3 rounded-xl bg-aeblack-50 p-3 text-xs">
      <div className="font-bold">
        {payload.outcome === 'approved' ? '✓' : '✗'} {payload.outcome}
      </div>
      {payload.reasons?.map((reason, i) => (
        <div key={i} className="mt-1 text-ink-soft">
          • {l(reason)}
        </div>
      ))}
    </div>
  );
}

/* --------------------------------------------------------------------------
 * ServicePublishDiff — collapsible per-section diff viewer (plan point 32):
 * top-level JSON sections fold via persisted SectionCard collapse; changed
 * paths are highlighted (green add / red remove / gold change) and unchanged
 * object keys stay dimmed behind a "show unchanged" toggle.
 * ------------------------------------------------------------------------- */
function ServicePublishDiff({
  slug,
  changelog,
  goldenRun,
}: {
  slug: string;
  changelog?: string;
  goldenRun?: { passed: boolean; generatedAt: string; scenarios: { id: string; lang: string; status: string; assertions: string[] }[] };
}) {
  const { l } = useI18n();
  const [showUnchanged, setShowUnchanged] = useState(false);
  const query = useQuery({
    queryKey: ['approval-service-diff', slug],
    queryFn: () => admin().serviceDiff(slug),
    enabled: Boolean(slug),
  });
  const entries = ((query.data?.entries ?? []) as SpecDiffEntry[]).slice(0, 30);
  const sections: { name: string; entries: SpecDiffEntry[] }[] = [];
  for (const entry of entries) {
    const name = diffSection(entry.path);
    const existing = sections.find((section) => section.name === name);
    if (existing) existing.entries.push(entry);
    else sections.push({ name, entries: [entry] });
  }
  return (
    <div className="mt-3 overflow-hidden rounded-xl border border-aeblack-100 bg-aeblack-50 text-xs">
      <div className="flex flex-wrap items-center gap-3 border-b border-aeblack-100 p-3">
        <div className="font-mono font-bold" dir="ltr">{slug}</div>
        {changelog && <div className="text-ink-soft">{changelog}</div>}
        <span className="ms-auto font-bold text-gold-deep">
          {query.isLoading ? l({ en: 'Loading diff…', ar: 'جارٍ تحميل الفروقات…' }) : `${entries.length} ${l({ en: 'changes', ar: 'تغييرات' })}`}
        </span>
        {entries.length > 0 && (
          <button
            type="button"
            onClick={() => setShowUnchanged((value) => !value)}
            aria-pressed={showUnchanged}
            className={`rounded-lg border px-2 py-1 text-[10px] font-bold transition ${
              showUnchanged
                ? 'border-aeblack-900 bg-aeblack-900 text-white'
                : 'border-aeblack-200 bg-white text-ink-soft hover:border-aeblack-300'
            }`}
          >
            {showUnchanged ? l(S.hideUnchanged) : l(S.showUnchanged)}
          </button>
        )}
      </div>
      {sections.length > 0 && (
        <div className="space-y-3 p-3">
          {sections.map((section) => (
            <SectionCard
              key={section.name}
              padded={false}
              accent={SECTION_ACCENTS.platform}
              collapseId={`approvals.diff.${slug}.${section.name}`}
              title={<code className="font-mono text-xs" dir="ltr">{section.name}</code>}
              actions={<span className="aegov-badge">{section.entries.length}</span>}
            >
              <div className="max-h-64 overflow-auto bg-white">
                {section.entries.map((entry) => (
                  <DiffEntryRow key={`${entry.path}-${entry.kind}`} entry={entry} showUnchanged={showUnchanged} />
                ))}
              </div>
            </SectionCard>
          ))}
        </div>
      )}
      {goldenRun && (
        <div className="border-t border-aeblack-100 bg-white p-3">
          <div className="mb-2 flex items-center gap-2">
            <strong>{l({ en: 'Golden conversation smoke', ar: 'اختبار المحادثة المرجعية' })}</strong>
            <StatusPill state={goldenRun.passed ? 'published' : 'rejected'} label={goldenRun.passed ? l({ en: 'Passed', ar: 'ناجح' }) : l({ en: 'Failed', ar: 'فاشل' })} />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            {goldenRun.scenarios.map((scenario) => (
              <div key={scenario.id} className="rounded-lg bg-aeblack-50 p-2">
                <div className="flex items-center justify-between"><code className="text-[10px] font-bold">{scenario.id}</code><StatusPill state={scenario.status === 'passed' ? 'published' : 'rejected'} label={scenario.status} /></div>
                <div className="mt-1 text-[9px] text-ink-faint">{scenario.assertions.join(' · ')}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

/** Top-level JSON section of a diff path (`journey.steps[0].x` → `journey`). */
function diffSection(path: string): string {
  const clean = path.replace(/^\/+/, '');
  const match = clean.match(/^[^./[\]]+/);
  return match?.[0] ?? '(root)';
}

const isPlainObject = (value: unknown): value is Record<string, unknown> =>
  typeof value === 'object' && value !== null && !Array.isArray(value);

const formatValue = (value: unknown): string => (value === undefined ? '—' : JSON.stringify(value));

function DiffEntryRow({ entry, showUnchanged }: { entry: SpecDiffEntry; showUnchanged: boolean }) {
  const accent =
    entry.kind === 'added' ? '#3f8e50' : entry.kind === 'removed' ? '#d83731' : '#b68a35';
  const objectRows =
    entry.kind === 'changed' && isPlainObject(entry.before) && isPlainObject(entry.after)
      ? Object.keys({ ...entry.before, ...entry.after }).map((key) => {
          const hasBefore = Object.prototype.hasOwnProperty.call(entry.before, key);
          const hasAfter = Object.prototype.hasOwnProperty.call(entry.after, key);
          const same =
            hasBefore &&
            hasAfter &&
            JSON.stringify((entry.before as Record<string, unknown>)[key]) ===
              JSON.stringify((entry.after as Record<string, unknown>)[key]);
          return {
            key,
            status: (!hasBefore ? 'added' : !hasAfter ? 'removed' : same ? 'same' : 'changed') as
              | 'added'
              | 'removed'
              | 'same'
              | 'changed',
            before: (entry.before as Record<string, unknown>)[key],
            after: (entry.after as Record<string, unknown>)[key],
          };
        })
      : null;
  const visibleRows = objectRows?.filter((row) => row.status !== 'same' || showUnchanged) ?? null;
  return (
    <div
      className="grid gap-2 border-b border-s-2 border-aeblack-50 p-2 last:border-0 md:grid-cols-[12rem_5rem_1fr]"
      style={{ borderInlineStartColor: accent }}
    >
      <code className="break-all font-bold">{entry.path}</code>
      <StatusPill state={entry.kind === 'removed' ? 'rejected' : entry.kind === 'added' ? 'published' : 'in_progress'} label={entry.kind} />
      {visibleRows ? (
        <div className="space-y-1" dir="ltr">
          {visibleRows.map((row) => (
            <div
              key={row.key}
              className={`rounded px-1.5 py-1 ${
                row.status === 'added'
                  ? 'bg-aegreen-50'
                  : row.status === 'removed'
                    ? 'bg-aered-50'
                    : row.status === 'changed'
                      ? 'bg-aegold-50'
                      : 'opacity-55'
              }`}
            >
              <span
                className={`text-[9px] font-bold ${
                  row.status === 'added'
                    ? 'text-aegreen-700'
                    : row.status === 'removed'
                      ? 'text-aered-600'
                      : row.status === 'changed'
                        ? 'text-gold-deep'
                        : 'text-ink-faint'
                }`}
              >
                {row.key}
              </span>
              <div className="mt-0.5 grid gap-1 sm:grid-cols-2">
                <pre className="overflow-hidden whitespace-pre-wrap rounded bg-white/70 p-1 text-[9px]">{formatValue(row.before)}</pre>
                <pre className="overflow-hidden whitespace-pre-wrap rounded bg-white/70 p-1 text-[9px]">{formatValue(row.after)}</pre>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="grid gap-1 sm:grid-cols-2" dir="ltr">
          {entry.kind !== 'added' && (
            <pre className={`overflow-hidden whitespace-pre-wrap rounded bg-aered-50 p-1.5 text-[9px] ${entry.kind === 'removed' ? 'sm:col-span-2' : ''}`}>
              {formatValue(entry.before)}
            </pre>
          )}
          {entry.kind !== 'removed' && (
            <pre className={`overflow-hidden whitespace-pre-wrap rounded bg-aegreen-50 p-1.5 text-[9px] ${entry.kind === 'added' ? 'sm:col-span-2' : ''}`}>
              {formatValue(entry.after)}
            </pre>
          )}
        </div>
      )}
    </div>
  );
}
